// Shared, presentation-only helpers for the Lead Workspace tabs. Every value
// formatted here comes from the real Nexceo::Lead/HumanTask/SiteVisit fields
// already returned by the existing APIs — nothing here invents data.

export function formatMoney(value, currency = 'INR') {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  const sym = currency === 'INR' ? '₹' : `${currency} `;
  if (num >= 10000000) return `${sym}${(num / 10000000).toFixed(num % 10000000 === 0 ? 0 : 1)} Cr`;
  if (num >= 100000) return `${sym}${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 1)} L`;
  return `${sym}${num.toLocaleString('en-IN')}`;
}

export function formatBudgetRange(lead) {
  if (!lead) return null;
  const min = formatMoney(lead.budget_min, lead.currency);
  const max = formatMoney(lead.budget_max, lead.currency);
  if (min && max && lead.budget_min !== lead.budget_max) return `${min} – ${max}`;
  return max || min || null;
}

export function formatDate(value, opts = {}) {
  if (!value) return null;
  return new Date(value).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...opts,
  });
}

export function formatDateTime(value) {
  if (!value) return null;
  const d = new Date(value);
  return `${d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}, ${d.toLocaleTimeString(
    'en-IN',
    { hour: 'numeric', minute: '2-digit' }
  )}`;
}

export function formatTime(value) {
  if (!value) return null;
  return new Date(value).toLocaleTimeString('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function timeAgo(value) {
  if (!value) return null;
  const diffMs = Date.now() - new Date(value).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(value);
}

// Only the allowed Tailwind palette for this app: woot, green, yellow,
// slate, black, red, violet (+ n-* tokens) — no blue/amber/gray/purple/etc.
export const PRIORITY_META = {
  urgent: { label: 'Urgent', cls: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400' },
  high: { label: 'High', cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/15 dark:text-yellow-400' },
  normal: { label: 'Normal', cls: 'bg-woot-100 text-woot-700 dark:bg-woot-500/15 dark:text-woot-400' },
  low: { label: 'Low', cls: 'bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-400' },
};

export function priorityMeta(priority) {
  return PRIORITY_META[priority] || PRIORITY_META.normal;
}

export const VERIFICATION_META = {
  ai_verified: {
    label: 'AI Verified',
    icon: 'i-lucide-bot',
    cls: 'bg-woot-100 text-woot-700 dark:bg-woot-500/15 dark:text-woot-400',
  },
  human_verified: {
    label: 'Human Verified',
    icon: 'i-lucide-user-check',
    cls: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400',
  },
  unverified: {
    label: 'Unverified',
    icon: 'i-lucide-clock',
    cls: 'bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-400',
  },
  rejected: {
    label: 'Rejected',
    icon: 'i-lucide-x-circle',
    cls: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400',
  },
};

export function verificationMeta(status) {
  return VERIFICATION_META[status] || VERIFICATION_META.unverified;
}

export function leadInitials(name) {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map(p => p[0])
    .join('')
    .toUpperCase();
}

export function digitsOnly(phone) {
  return (phone || '').replace(/[^\d]/g, '');
}

export function humanize(value) {
  if (!value) return null;
  return String(value)
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
